/**
 * API error handling utilities
 */
import { ERROR_MESSAGES, HTTP_STATUS } from '../constants';
import type { ApiError, ApiErrorResponseBody, ValidationErrors } from '../types';

const TECHNICAL_ERROR_PATTERN =
  /sql|database|dberror|sqlite|postgres|npgsql|mysql|oracle|deadlock|stack trace|exception|inner exception|object reference|nullreference|timeout expired|connection (pool|was closed|reset|forcibly)|cannot insert|violation of|system\.(data|invalidoperation|argument)|axioserror|request failed with status|econnreset|enotfound|enetunreach|socket hang up|i\/o error|at [a-z0-9_$.]+\(/i;

/**
 * Thrown when a request answered but carried nothing usable — a missing payload
 * or `success: false`. List endpoints must raise this instead of returning an
 * empty page, or the screen presents a failure as "nothing here yet".
 *
 * The `message` is generic and safe to display directly; the `endpoint` property
 * is kept separately for logging. `getUserFacingErrorMessage` returns the
 * caller's own fallback copy, but if a consumer ever reads `Error.message`
 * directly it will not leak internal endpoint names.
 */
class UnusableResponseError extends Error {
  readonly endpoint: string;

  constructor(endpoint: string) {
    super('The server response was not usable');
    this.name = 'UnusableResponseError';
    this.endpoint = endpoint;
  }
}

/**
 * True when a backend message is a technical/database/exception dump, not UX copy.
 */
const isTechnicalServerMessage = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) {
    return true;
  }
  if (trimmed.length > 180) {
    return true;
  }
  if (trimmed.includes('\n') && trimmed.length > 80) {
    return true;
  }
  return TECHNICAL_ERROR_PATTERN.test(trimmed);
};

const userSafeServerMessage = (
  serverMessage: string | null | undefined,
  fallback: string,
): string => {
  if (!serverMessage) {
    return fallback;
  }
  if (isTechnicalServerMessage(serverMessage)) {
    return fallback;
  }
  return serverMessage;
};

/**
 * The status the message should be read against. An API that rejects a request
 * inside the body (`{ statusCode: 400, message: "Email already exists." }`)
 * sometimes still sends it over HTTP 500, and reading only the transport status
 * would replace its explanation with generic server-fault copy.
 */
const effectiveStatus = (
  status: number,
  body: ApiErrorResponseBody | undefined,
): number => {
  const declared = body?.statusCode;
  return typeof declared === 'number' && declared >= 400 && declared < 600
    ? declared
    : status;
};

/**
 * Extract a user-friendly message from an Axios-style error.
 */
const getApiErrorMessage = (error: ApiError): string => {
  // Network error or pre-request exception (no HTTP response)
  if (!error.response) {
    if (
      error.code === 'ECONNABORTED' ||
      error.code === 'ETIMEDOUT' ||
      (typeof error.message === 'string' && error.message.toLowerCase().includes('timeout'))
    ) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }

    const isAxiosNetworkFailure =
      Boolean(error.request) ||
      error.isAxiosError === true ||
      error.code === 'ERR_NETWORK' ||
      error.message === 'Network Error';

    if (isAxiosNetworkFailure) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }

    if (error instanceof Error && error.message) {
      return isTechnicalServerMessage(error.message)
        ? ERROR_MESSAGES.SERVER_ERROR
        : error.message;
    }

    return ERROR_MESSAGES.SERVER_ERROR;
  }

  const { status, data } = error.response;
  const body: ApiErrorResponseBody | undefined =
    data && typeof data === 'object' ? data : undefined;

  const firstValidationError = (() => {
    if (body?.errors && typeof body.errors === 'object') {
      const firstKey = Object.keys(body.errors)[0];
      if (firstKey) {
        const msgs = (body.errors as Record<string, string | string[]>)[firstKey];
        return Array.isArray(msgs) ? msgs[0] : msgs;
      }
    }
    return null;
  })();

  const serverMessage =
    body?.message ||
    body?.error ||
    body?.detail ||
    firstValidationError ||
    (typeof body?.title === 'string' ? body.title : null) ||
    (typeof data === 'string' ? data : null);

  const resolvedStatus = effectiveStatus(status, body);

  // A server fault describes itself for an operator ("An unexpected error
  // occurred while fetching affiliates"), never for the person who pressed the
  // button. A gateway or an overloaded upstream (502, 503, 504) has nothing to
  // tell them either.
  if (resolvedStatus >= 500) {
    return ERROR_MESSAGES.SERVER_ERROR;
  }

  switch (resolvedStatus) {
    case HTTP_STATUS.BAD_REQUEST:
      return userSafeServerMessage(
        serverMessage,
        'Invalid request. Please check your input.',
      );
    case HTTP_STATUS.UNAUTHORIZED:
      return userSafeServerMessage(serverMessage, ERROR_MESSAGES.UNAUTHORIZED);
    case HTTP_STATUS.FORBIDDEN:
      return userSafeServerMessage(
        serverMessage,
        'You do not have permission to perform this action.',
      );
    case HTTP_STATUS.NOT_FOUND:
      return userSafeServerMessage(
        serverMessage,
        'The requested resource was not found.',
      );
    default:
      return userSafeServerMessage(serverMessage, ERROR_MESSAGES.SERVER_ERROR);
  }
};

/**
 * User-facing copy for any thrown value — Axios errors go through
 * `getApiErrorMessage`; local Error messages are kept unless they look technical.
 */
const getUserFacingErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof UnusableResponseError) {
    return fallback;
  }
  const apiLike = error as ApiError;
  if (apiLike?.response || apiLike?.code) {
    return getApiErrorMessage(apiLike);
  }
  if (error instanceof Error && error.message) {
    return isTechnicalServerMessage(error.message) ? fallback : error.message;
  }
  return fallback;
};

/**
 * Parse validation errors from a 422 or 400 response.
 */
const parseValidationErrors = (error: ApiError): ValidationErrors => {
  const errors: ValidationErrors = {};
  if (!error.response) return errors;

  const { data } = error.response;
  const body: ApiErrorResponseBody | undefined =
    data && typeof data === 'object' ? data : undefined;

  if (body?.errors && typeof body.errors === 'object') {
    Object.keys(body.errors).forEach(key => {
      const messages = body.errors?.[key];
      errors[key] = Array.isArray(messages) ? messages[0] : messages ?? '';
    });
  }
  return errors;
};

export type { ApiError, ApiErrorResponseBody, ValidationErrors };

export {
  getApiErrorMessage,
  getUserFacingErrorMessage,
  parseValidationErrors,
  UnusableResponseError,
};
